import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as Icons from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner';
import webApiFormService from '../services/webApiFormService';

const FaTimes = Icons.FaTimes as any;
const FaHistory = Icons.FaHistory as any;
const FaUndo = Icons.FaUndo as any;
const FaCheckCircle = Icons.FaCheckCircle as any;
const FaCopy = Icons.FaCopy as any;

interface FormHistory {
  uid: string;
  code?: string;
  status: string;
  Status?: string;
  modified_time?: string;
  modifiedTime?: string;
  server_add_time?: string;
  server_modified_time?: string;
  created_time?: string;
  modified_by?: string;
  modifiedBy?: string;
  user_name?: string;
  assigned_to?: string;
  assignedTo?: string;
  AssignedTo?: string;
  revert_remark?: string;
  revertRemark?: string;
  remarks?: string;
  Remarks?: string;
  RevertRemarks?: string;
}

interface TrackingHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordUid: string;
  ticketNumber: string;
}

const TrackingHistoryModal: React.FC<TrackingHistoryModalProps> = ({
  isOpen,
  onClose,
  recordUid,
  ticketNumber
}) => {
  const [history, setHistory] = useState<FormHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedLoginId, setCopiedLoginId] = useState<string | null>(null);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(ticketNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLoginId = (loginId: string, recordUid: string) => {
    navigator.clipboard.writeText(loginId);
    setCopiedLoginId(recordUid);
    setTimeout(() => setCopiedLoginId(null), 2000);
  };

  useEffect(() => {
    if (isOpen && recordUid) {
      loadHistory();
    }
  }, [isOpen, recordUid]);

  const loadHistory = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await webApiFormService.getFormHistory(recordUid);

      if (response.success && response.data) {
        if (response.data.length > 0) {
          setHistory(response.data);
        } else {
          setError('No history records found');
        }
      } else {
        setError(response.message || 'No history records found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load history. Please check your authentication.');
    } finally {
      setIsLoading(false);
    }
  };

  const isRevertRecord = (record: FormHistory): boolean => {
    const revertRemark = record.revert_remark || record.revertRemark || record.RevertRemarks || record.remarks || record.Remarks || '';
    return revertRemark.trim() !== '';
  };

  const getRevertRemark = (record: FormHistory): string => {
    return record.revert_remark || record.revertRemark || record.RevertRemarks || record.remarks || record.Remarks || '';
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={onClose} />
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            <FaHistory style={{ marginRight: '0.5rem' }} />
            History - {ticketNumber}
          </ModalTitle>
          <CopyButton onClick={copyToClipboard} title="Copy ticket number">
            {React.createElement(FaCopy, { size: 12 })}
            {copied ? 'Copied!' : 'Copy'}
          </CopyButton>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          {isLoading ? (
            <LoadingContainer>
              <LoadingSpinner isVisible={true} />
              <LoadingText>Loading history...</LoadingText>
            </LoadingContainer>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : history.length === 0 ? (
            <EmptyMessage>No history records found</EmptyMessage>
          ) : (
            <Timeline>
              {history.map((record, index) => {
                const isRevert = isRevertRecord(record);
                const status = record.status || record.Status || 'Unknown';
                const userName = record.user_name || record.modified_by || record.modifiedBy || 'System';
                const assignedTo = record.assigned_to || record.assignedTo || record.AssignedTo || '';
                const dateTime = formatDateTime(
                  record.server_add_time ||
                  record.server_modified_time ||
                  record.created_time ||
                  record.modified_time ||
                  record.modifiedTime
                );
                const revertRemark = getRevertRemark(record);
                const recordKey = record.uid || record.code || index.toString();

                return (
                  <TimelineItem key={recordKey}>
                    <TimelineMarker isRevert={isRevert}>
                      {isRevert ? <FaUndo /> : <FaCheckCircle />}
                    </TimelineMarker>
                    <TimelineContent>
                      <TimelineHeader>
                        <StatusText isRevert={isRevert}>{status}</StatusText>
                        {isRevert && <RevertBadge>REVERT</RevertBadge>}
                      </TimelineHeader>
                      <TimelineDetails>
                        <DetailRow>
                          <DetailLabel>Modified By:</DetailLabel>
                          <DetailValue>{userName}</DetailValue>
                        </DetailRow>
                        {assignedTo && (
                          <DetailRow>
                            <DetailLabel>Assigned To:</DetailLabel>
                            <AssignedToContainer>
                              <DetailValue>{assignedTo}</DetailValue>
                              <CopyLoginButton
                                onClick={() => copyLoginId(assignedTo, recordKey)}
                                title="Copy login ID"
                              >
                                {React.createElement(FaCopy, { size: 10 })}
                                {copiedLoginId === recordKey ? 'Copied!' : 'Copy'}
                              </CopyLoginButton>
                            </AssignedToContainer>
                          </DetailRow>
                        )}
                        <DetailRow>
                          <DetailLabel>Date & Time:</DetailLabel>
                          <DetailValue>{dateTime}</DetailValue>
                        </DetailRow>
                        {isRevert && revertRemark && (
                          <RemarkRow>
                            <DetailLabel>Revert Remarks:</DetailLabel>
                            <RemarkValue>{revertRemark}</RemarkValue>
                          </RemarkRow>
                        )}
                      </TimelineDetails>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          )}
        </ModalBody>

        <ModalFooter>
          <CloseBtn onClick={onClose}>Close</CloseBtn>
        </ModalFooter>
      </ModalContainer>
    </>
  );
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: scaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

  @keyframes scaleUp {
    0% { 
      transform: translate(-50%, -50%) scale(0.3);
      opacity: 0;
    }
    100% { 
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: white;
  color: ${props => props.theme.textPrimary};
  border-bottom: 2px solid ${props => props.theme.primary || '#F40000'};
  border-radius: 12px 12px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  color: ${props => props.theme.primary || '#F40000'};
`;

const CopyButton = styled.button`
  background: white;
  border: 1px solid ${props => props.theme.primary || '#F40000'};
  color: ${props => props.theme.primary || '#F40000'};
  cursor: pointer;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  transition: all 0.2s;
  margin: 0 0.5rem;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary || '#F40000'};
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
    border-radius: 50%;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const LoadingText = styled.p`
  margin-top: 1rem;
  color: ${props => props.theme.textSecondary};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.error || '#d32f2f'};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.textSecondary};
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 0.5rem;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${props => props.theme.border || '#e0e0e0'};
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 0.75rem;

  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineMarker = styled.div<{ isRevert?: boolean }>`
  position: absolute;
  left: -0.875rem;
  top: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: ${props => props.isRevert ? '#f39c12' : props.theme.primary || '#F40000'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
  font-size: 0.7rem;
`;

const TimelineContent = styled.div`
  background: ${props => props.theme.surface || '#f5f5f5'};
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  margin-left: 1rem;
  border-left: 2px solid ${props => props.theme.primary || '#F40000'};
`;

const TimelineHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
`;

const StatusText = styled.h3<{ isRevert?: boolean }>`
  margin: 0;
  font-size: 0.85rem;
  color: ${props => props.isRevert ? '#f39c12' : props.theme.textPrimary};
  font-weight: 600;
`;

const RevertBadge = styled.span`
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  color: white;
  padding: 0.15rem 0.35rem;
  border-radius: 3px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const TimelineDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 0.4rem;
  font-size: 0.75rem;
`;

const DetailLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-weight: 500;
  min-width: 70px;
`;

const DetailValue = styled.span`
  color: ${props => props.theme.textPrimary};
`;

const AssignedToContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const CopyLoginButton = styled.button`
  background: white;
  border: 1px solid ${props => props.theme.primary || '#F40000'};
  color: ${props => props.theme.primary || '#F40000'};
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(244, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const RemarkRow = styled.div`
  margin-top: 0.4rem;
  padding-top: 0.4rem;
  border-top: 1px solid ${props => props.theme.border || '#e0e0e0'};
`;

const RemarkValue = styled.div`
  color: ${props => props.theme.textPrimary};
  font-size: 0.75rem;
  font-style: italic;
  background: white;
  padding: 0.4rem 0.5rem;
  border-radius: 4px;
  margin-top: 0.3rem;
  border-left: 2px solid #f39c12;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${props => props.theme.border || '#e0e0e0'};
  display: flex;
  justify-content: flex-end;
`;

const CloseBtn = styled.button`
  background: ${props => props.theme.primary || '#F40000'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.theme.primaryDark || '#c00000'};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default TrackingHistoryModal;
